import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { generateScreenplayHTML } from '../utils/screenplayTemplate';
import { exportToPDF } from '../utils/pdfExport';
import { useConfirmDialog } from '../components/ConfirmDialog';
import {
  decryptStoryPayload,
  encryptScreenplayElementPayload,
  encryptStoryTextPayload,
} from '../e2ee/client';
import './StoryEditor.css';

type TabType = 'story' | 'screenplay';

interface ScreenplayElement {
  id: number;
  element_type: 'dialogue' | 'narration' | 'location';
  character_name?: string;
  content: string;
  element_order: number;
}

interface Scene {
  id: number;
  scene_number: number;
  elements: ScreenplayElement[];
}

interface StoryPayload {
  title: string;
  story_type?: 'written' | 'upload';
  story_text?: string;
  file_name?: string | null;
  file_path?: string | null;
  scenes?: Scene[];
}

interface StoryCharacter {
  id: number;
  name: string;
  description?: string;
}

interface GroupMember {
  user_id: number;
  name: string;
  email: string;
  profile_picture?: string | null;
}

interface UserWithStoryAccess {
  user_id: number;
  name: string;
  email: string;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────
// Character Panel
// ─────────────────────────────────────────────────────────────
interface CharacterPanelProps {
  characters: StoryCharacter[];
  onAdd: (name: string, description: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  isReadOnly: boolean;
}

const CharacterPanel: React.FC<CharacterPanelProps> = ({
  characters, onAdd, onDelete, isReadOnly,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [newName, setNewName]   = useState('');
  const [newDesc, setNewDesc]   = useState('');
  const [adding, setAdding]     = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError]       = useState('');

  const handleAdd = async () => {
    if (!newName.trim()) { setError('Character name is required'); return; }
    setAdding(true); setError('');
    try {
      await onAdd(newName.trim(), newDesc.trim());
      setNewName(''); setNewDesc(''); setFormOpen(false);
    } catch (e: any) {
      setError(e.message || 'Failed to add character');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="character-panel">
      <button
        className="character-panel-toggle"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span className="character-panel-toggle-icon">🎭</span>
        <span className="character-panel-toggle-label">
          Characters
          <span className="character-count-badge">{characters.length}</span>
        </span>
        <span className={`character-panel-chevron${expanded ? ' open' : ''}`}>›</span>
      </button>

      {expanded && (
        <div className="character-panel-body">
          {characters.length === 0 ? (
            <div className="character-empty-state">
              No characters yet. Add your first character to use in screenplay dialogues.
            </div>
          ) : (
            <div className="character-chips">
              {characters.map(ch => (
                <div key={ch.id} className="character-chip">
                  <span className="character-chip-initial">{ch.name.charAt(0).toUpperCase()}</span>
                  <div className="character-chip-info">
                    <span className="character-chip-name">{ch.name}</span>
                    {ch.description && (
                      <span className="character-chip-desc">{ch.description}</span>
                    )}
                  </div>
                  {!isReadOnly && (
                    <button
                      className="character-chip-delete"
                      onClick={() => onDelete(ch.id)}
                      aria-label={`Delete ${ch.name}`}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isReadOnly && (
            <div className="character-add-section">
              {!formOpen ? (
                <button className="character-add-open-btn" onClick={() => setFormOpen(true)}>
                  + Add Character
                </button>
              ) : (
                <div className="character-add-form">
                  {error && <div className="character-form-error">{error}</div>}
                  <input
                    className="character-form-input"
                    placeholder="Character name *"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleAdd();
                      if (e.key === 'Escape') setFormOpen(false);
                    }}
                    autoFocus
                  />
                  <input
                    className="character-form-input"
                    placeholder="Brief description (optional)"
                    value={newDesc}
                    onChange={e => setNewDesc(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                  />
                  <div className="character-form-actions">
                    <button className="character-form-save" onClick={handleAdd} disabled={adding}>
                      {adding ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      className="character-form-cancel"
                      onClick={() => { setFormOpen(false); setError(''); setNewName(''); setNewDesc(''); }}
                    >Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Manage Access Modal
// ─────────────────────────────────────────────────────────────
interface ManageAccessModalProps {
  storyId: string;
  onClose: () => void;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const getMediaUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (key.startsWith('http')) return key;
  return `${BACKEND_URL}/uploads/${key}`;
};

const getDisplayFileName = (value?: string | null) => {
  if (!value) return 'Uploaded file';
  const normalized = value.replace(/\\/g, '/');
  const lastSegment = normalized.split('/').filter(Boolean).pop() || value;
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
};

const ManageAccessModal: React.FC<ManageAccessModalProps> = ({ storyId, onClose }) => {
  const { confirm, confirmDialog } = useConfirmDialog();
  const [groupMembers, setGroupMembers]       = useState<GroupMember[]>([]);
  const [usersWithAccess, setUsersWithAccess] = useState<UserWithStoryAccess[]>([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState('');
  const [message, setMessage]                 = useState('');

  const flashError   = (msg: string) => { setError(msg);   setTimeout(() => setError(''),   2500); };
  const flashMessage = (msg: string) => { setMessage(msg); setTimeout(() => setMessage(''), 2000); };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => { loadData(); }, [storyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersRes, accessRes] = await Promise.all([
        api.get(`/groups/${storyId}/members`),
        api.get(`/stories/${storyId}/screenplay-access`),
      ]);
      setGroupMembers(membersRes.data.members || []);
      setUsersWithAccess(accessRes.data.usersWithAccess || []);
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleGrant = async (userId: number) => {
    try {
      await api.post(`/stories/${storyId}/screenplay-access`, { targetUserId: userId });
      flashMessage('Editing access granted');
      loadData();
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to grant access');
    }
  };

  const handleRevoke = async (userId: number) => {
    const approved = await confirm({
      title: 'Revoke Access',
      message: 'Revoke story and screenplay editing access for this user?',
      confirmLabel: 'Revoke Access',
      cancelLabel: 'Keep Access',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/stories/${storyId}/screenplay-access/${userId}`);
      flashMessage('Editing access revoked');
      loadData();
    } catch (err: any) {
      flashError(err.response?.data?.message || 'Failed to revoke access');
    }
  };

  return (
    <div className="se-access-overlay" onClick={onClose}>
      <div className="se-access-modal" onClick={e => e.stopPropagation()}>
        {confirmDialog}

        <div className="se-access-header">
          <div className="se-access-title">
            <span className="se-access-label">Manage</span>
            <span className="se-access-name">Story & Screenplay Access</span>
          </div>
          <button className="se-access-close" onClick={onClose}>✕</button>
        </div>

        <p className="se-access-hint">
          Grant group members permission to edit the screenplay — add scenes, elements, and dialogue.
        </p>

        <p className="se-access-hint">
          Story editors can update story text, title, files, characters, and the screenplay.
        </p>

        {error   && <div className="se-access-flash se-access-flash--error">{error}</div>}
        {message && <div className="se-access-flash se-access-flash--ok">{message}</div>}

        <div className="se-access-body">
          {loading ? (
            <div className="se-access-empty">Loading members…</div>
          ) : groupMembers.length === 0 ? (
            <div className="se-access-empty">No group members found.</div>
          ) : (
            <div className="se-access-list">
              {groupMembers.map(member => {
                const dpUrl     = getMediaUrl(member.profile_picture);
                const hasAccess = usersWithAccess.some(u => u.user_id === member.user_id);
                return (
                  <div key={member.user_id} className="se-access-row">
                    <div className="se-access-avatar">
                      {dpUrl ? (
                        <img
                          src={dpUrl}
                          alt={member.name}
                          onError={e => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        member.name?.charAt(0).toUpperCase() ?? '?'
                      )}
                    </div>
                    <div className="se-access-info">
                      <p className="se-access-member-name">{member.name || 'Unknown'}</p>
                      <p className="se-access-member-email">{member.email || ''}</p>
                    </div>
                    <div className="se-access-badge-wrap">
                      {hasAccess && (
                        <span className="se-access-has-badge">Can Edit</span>
                      )}
                    </div>
                    {hasAccess ? (
                      <button
                        className="se-access-btn se-access-btn--revoke"
                        onClick={() => handleRevoke(member.user_id)}
                      >Revoke</button>
                    ) : (
                      <button
                        className="se-access-btn se-access-btn--grant"
                        onClick={() => handleGrant(member.user_id)}
                      >Grant</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
const StoryEditorPage: React.FC = () => {
  const navigate    = useNavigate();
  const { storyId } = useParams<{ storyId?: string }>();
  const { user }    = useAuth();
  const { confirm, notify, confirmDialog } = useConfirmDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const storyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const elementTextareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const storyIdRef = useRef(storyId);

  useEffect(() => {
    storyIdRef.current = storyId;
  }, [storyId]);

  const [activeTab, setActiveTab]   = useState<TabType>('story');
  const [title, setTitle]           = useState('');
  const [storyText, setStoryText]   = useState('');
  const [storyType, setStoryType]   = useState<'written' | 'upload'>('written');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedFileUrl, setUploadedFileUrl]   = useState('');
  const [scenes, setScenes]         = useState<Scene[]>([]);
  const [characters, setCharacters] = useState<StoryCharacter[]>([]);

  const [loading, setLoading]               = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [error, setError]                   = useState('');
  const [message, setMessage]               = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle]           = useState('');
  const [isOwner, setIsOwner]               = useState(true);
  const [hasActiveGroup, setHasActiveGroup] = useState(false);
  const [hasScreenplayAccess, setHasScreenplayAccess] = useState(false);
  const [deletingDraft, setDeletingDraft]   = useState(false);

  // Access modal
  const [showAccessModal, setShowAccessModal] = useState(false);

  // ── Scene dropdown selection ──────────────────────────────
  const [activeSceneId, setActiveSceneId] = useState<number | null>(null);

  useEffect(() => {
    if (storyId && storyId !== 'new') {
      loadStory();
      checkOwnership();
      checkIfGroupExists();
      loadCharacters();
    } else {
      getNextUntitledTitle();
    }
  }, [storyId]);

  useEffect(() => {
    if (!storyId || storyId === 'new') return;
    api.get(`/stories/${storyId}/screenplay-access/me`)
      .then(res => setHasScreenplayAccess(res.data.hasAccess))
      .catch(() => setHasScreenplayAccess(false));
  }, [storyId]);

  // When scenes load, default to the first scene
  useEffect(() => {
    if (scenes.length > 0 && activeSceneId === null) {
      setActiveSceneId(scenes[0].id);
    }
    // If the active scene was deleted, reset to first
    if (activeSceneId !== null && !scenes.find(s => s.id === activeSceneId)) {
      setActiveSceneId(scenes.length > 0 ? scenes[0].id : null);
    }
  }, [scenes]);

  const canEditStory = isOwner || hasScreenplayAccess;
  const canEditScreenplay = canEditStory;
  const isReadOnly = !canEditStory;
  const activeScene = scenes.find(s => s.id === activeSceneId) ?? null;

  const resizeTextarea = useCallback((textarea: HTMLTextAreaElement | null, minHeight: number) => {
    if (!textarea) return;
    textarea.style.height = '0px';
    textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    textarea.style.overflowY = 'hidden';
  }, []);

  const setStoryTextareaNode = useCallback(
    (node: HTMLTextAreaElement | null) => {
      storyTextareaRef.current = node;
      resizeTextarea(node, 480);
    },
    [resizeTextarea],
  );

  const setElementTextareaNode = useCallback(
    (elementId: number, minHeight: number, node: HTMLTextAreaElement | null) => {
      elementTextareaRefs.current[elementId] = node;
      resizeTextarea(node, minHeight);
    },
    [resizeTextarea],
  );

  useEffect(() => {
    resizeTextarea(storyTextareaRef.current, 480);
  }, [resizeTextarea, storyText]);

  useEffect(() => {
    if (!activeScene) return;
    activeScene.elements.forEach((element) => {
      resizeTextarea(
        elementTextareaRefs.current[element.id],
        element.element_type === 'location' ? 60 : 110,
      );
    });
  }, [activeScene, resizeTextarea]);

  // ── Character CRUD ────────────────────────────────────────

  const loadCharacters = useCallback(async () => {
    if (!storyId || storyId === 'new') return;
    try {
      const res = await api.get(`/stories/${storyId}/characters`);
      setCharacters(res.data.characters || []);
    } catch { /* silent */ }
  }, [storyId]);

  const handleAddCharacter = async (name: string, description: string) => {
    if (!storyId || storyId === 'new') throw new Error('Save the story first');
    const res = await api.post(`/stories/${storyId}/characters`, { name, description });
    setCharacters(prev => [...prev, res.data.character]);
  };

  const handleDeleteCharacter = async (characterId: number) => {
    const approved = await confirm({
      title: 'Delete Character',
      message: 'Delete this character? Any dialogue lines assigned to them will keep their name as plain text.',
      confirmLabel: 'Delete Character',
      cancelLabel: 'Keep Character',
      danger: true,
    });
    if (!approved) return;
    await api.delete(`/stories/${storyId}/characters/${characterId}`);
    setCharacters(prev => prev.filter(c => c.id !== characterId));
  };

  // ── Story loading ─────────────────────────────────────────

  const checkIfGroupExists = async () => {
    if (!storyId || storyId === 'new') { setHasActiveGroup(false); return; }
    try {
      const response = await api.get(`/groups/${storyId}`);
      setHasActiveGroup(response.data.group.is_active);
    } catch { setHasActiveGroup(false); }
  };

  const checkOwnership = async () => {
    try {
      const response = await api.get(`/groups/${storyId}/ownership`);
      setIsOwner(response.data.isOwner);
    } catch {
      setIsOwner(true);
    }
  };

  const getNextUntitledTitle = async () => {
    try {
      const response = await api.get('/next-untitled');
      setTitle(response.data.title);
      setTempTitle(response.data.title);
    } catch {
      setTitle('Untitled1');
      setTempTitle('Untitled1');
    }
  };

  const loadStory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/stories/${storyId}`);
      const rawStory: StoryPayload = response.data.story;
      setTitle(rawStory.title);
      setTempTitle(rawStory.title);
      setStoryType(rawStory.story_type || 'written');

      if (rawStory.story_type === 'written') {
        setStoryText(rawStory.story_text || '');
      } else if (rawStory.story_type === 'upload' && rawStory.file_path) {
        const initialFileName = rawStory.file_name || getDisplayFileName(rawStory.file_path);
        setUploadedFileName(initialFileName);
        try {
          const urlRes = await api.get(`/stories/${storyId}/file-url`);
          setUploadedFileUrl(urlRes.data.fileUrl);
          setUploadedFileName(urlRes.data.fileName || initialFileName);
        } catch { setStoryType('written'); }
      }
      setScenes(rawStory.scenes || []);

      if (user?.id) {
        setTimeout(() => {
          void (async () => {
            try {
              const story = await decryptStoryPayload(user.id, Number(storyId), rawStory);
              if (storyIdRef.current !== storyId) return;
              setTitle(story.title);
              setTempTitle(story.title);
              setStoryType(story.story_type || 'written');
              if (story.story_type === 'written') {
                setStoryText(story.story_text || '');
              }
              setScenes(story.scenes || []);
            } catch (error: any) {
              setError(error?.message || 'Opened story, but could not unlock encrypted content yet.');
            }
          })();
        }, 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load story');
    } finally {
      setLoading(false);
    }
  };

  // ── File upload ───────────────────────────────────────────

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['.txt', '.doc', '.docx', '.pdf'];
    const isValid    = validTypes.some(type => file.name.toLowerCase().endsWith(type));
    if (!isValid) { setError('Please upload a .txt, .doc, .docx, or .pdf file'); return; }

    let currentStoryId = storyId;
    if (!currentStoryId || currentStoryId === 'new') {
      try {
        const createRes = await api.post('/stories', { title });
        currentStoryId  = String(createRes.data.story.id);
        navigate(`/story/${currentStoryId}`, { replace: true });
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to create story before upload');
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true); setError('');
      const response = await api.post(
        `/stories/${currentStoryId}/upload-file`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setUploadedFileName(file.name);
      setUploadedFileUrl(response.data.fileUrl);
      setStoryType('upload');
      setMessage('File uploaded successfully');
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async () => {
    const approved = await confirm({
      title: 'Remove File',
      message: 'Delete this file and reset the story back to text mode?',
      confirmLabel: 'Remove File',
      cancelLabel: 'Keep File',
      danger: true,
    });
    if (!approved) return;
    try {
      if (storyId && storyId !== 'new') await api.delete(`/stories/${storyId}/file`);
      setUploadedFileName(''); setUploadedFileUrl('');
      setStoryType('written'); setStoryText('');
      setMessage('File removed');
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete file');
    }
  };

  const createDraftStory = useCallback(async (): Promise<string | null> => {
    try {
      const createResponse = await api.post('/stories', { title });
      const newStoryId      = String(createResponse.data.story.id);

      const payload =
        user?.id
          ? await encryptStoryTextPayload(user.id, Number(newStoryId), storyText || '')
          : { storyType: 'written', storyText: storyText || '', fileName: null };

      await api.put(`/stories/${newStoryId}/content`, payload);
      navigate(`/story/${newStoryId}`, { replace: true });
      return newStoryId;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create story draft');
      return null;
    }
  }, [title, storyText, user, navigate]);

  const handleSaveStory = async () => {
    setError(''); setMessage(''); setLoading(true);
    try {
      if (storyId && storyId !== 'new') {
        if (storyType === 'upload') {
          await api.put(`/stories/${storyId}/content`, {
            storyType: 'upload',
            fileName: uploadedFileName,
            storyText: '',
          });
        } else if (user?.id) {
          const payload = await encryptStoryTextPayload(user.id, Number(storyId), storyText);
          await api.put(`/stories/${storyId}/content`, payload);
        } else {
          await api.put(`/stories/${storyId}/content`, {
            storyType: 'written',
            storyText,
            fileName: null,
          });
        }
        setMessage('Story saved');
      } else {
        const createResponse = await api.post('/stories', { title });
        const newStoryId     = createResponse.data.story.id;
        const payload =
          user?.id
            ? await encryptStoryTextPayload(user.id, Number(newStoryId), storyText)
            : { storyType: 'written', storyText, fileName: null };
        await api.put(`/stories/${newStoryId}/content`, payload);
        setMessage('Story created');
        setTimeout(() => navigate(`/story/${newStoryId}`, { replace: true }), 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save story');
    } finally {
      setLoading(false);
    }
  };

  // ── Screenplay scene / element CRUD ──────────────────────

  const handleAddScene = async () => {
    let currentStoryId = storyId;
    if (!currentStoryId || currentStoryId === 'new') {
      const createdId = await createDraftStory();
      if (!createdId) return;
      currentStoryId = createdId;
    }

    try {
      const response = await api.post(`/stories/${currentStoryId}/scenes`);
      const newScene = response.data.scene;
      setScenes(prev => [...prev, newScene]);
      setActiveSceneId(newScene.id);
      setMessage('Scene added');
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add scene');
    }
  };

  const handleDeleteScene = async (sceneId: number) => {
    const approved = await confirm({
      title: 'Delete Scene',
      message: 'Delete this scene from the screenplay?',
      confirmLabel: 'Delete Scene',
      cancelLabel: 'Keep Scene',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/stories/${storyId}/scenes/${sceneId}`);
      setScenes(prev => prev.filter(s => s.id !== sceneId));
      setMessage('Scene deleted');
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete scene');
    }
  };

  const handleAddElement = async (
    sceneId: number,
    elementType: 'dialogue' | 'narration' | 'location'
  ) => {
    try {
      const payload =
        storyId && user?.id
          ? await encryptScreenplayElementPayload(
              user.id,
              Number(storyId),
              elementType === 'dialogue' ? '' : null,
              '',
            )
          : { characterName: elementType === 'dialogue' ? '' : null, content: '' };
      const response = await api.post(
        `/stories/${storyId}/scenes/${sceneId}/elements`,
        { elementType, ...payload },
      );
      setScenes(prev => prev.map(scene =>
        scene.id === sceneId
          ? { ...scene, elements: [...scene.elements, response.data.element] }
          : scene,
      ));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add element');
    }
  };

  const handleElementChange = async (
    elementId: number,
    field: 'character_name' | 'content',
    value: string,
  ) => {
    setScenes(prev => prev.map(scene => ({
      ...scene,
      elements: scene.elements.map(element =>
        element.id === elementId ? { ...element, [field]: value } : element,
      ),
    })));
    try {
      const element = scenes.flatMap(s => s.elements).find(e => e.id === elementId);
      if (element) {
        const payload =
          storyId && user?.id
            ? await encryptScreenplayElementPayload(
                user.id,
                Number(storyId),
                field === 'character_name' ? value : element.character_name,
                field === 'content' ? value : element.content,
              )
            : {
                characterName: field === 'character_name' ? value : element.character_name,
                content: field === 'content' ? value : element.content,
              };
        await api.put(`/stories/${storyId}/elements/${elementId}`, payload);
      }
    } catch (err: any) {
      console.error('Failed to update element:', err);
    }
  };

  const handleDeleteElement = async (sceneId: number, elementId: number) => {
    const approved = await confirm({
      title: 'Delete Element',
      message: 'Delete this screenplay element?',
      confirmLabel: 'Delete Element',
      cancelLabel: 'Keep Element',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/stories/${storyId}/elements/${elementId}`);
      setScenes(prev => prev.map(scene =>
        scene.id === sceneId
          ? { ...scene, elements: scene.elements.filter(e => e.id !== elementId) }
          : scene,
      ));
      setMessage('Element deleted');
      setTimeout(() => setMessage(''), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete element');
    }
  };

  const handleExportScreenplay = async () => {
    if (scenes.length === 0) { setError('No scenes to export. Please add scenes first.'); return; }
    try {
      const htmlContent = generateScreenplayHTML(title, scenes, user?.name || 'Unknown Author');
      const opened = exportToPDF(htmlContent, title);
      if (!opened) {
        await notify({
          title: 'Popup Blocked',
          message: 'Please allow popups in your browser to export the screenplay PDF.',
          confirmLabel: 'OK',
        });
        return;
      }
      setMessage('Screenplay exported');
      setTimeout(() => setMessage(''), 2000);
    } catch { setError('Failed to export screenplay'); }
  };

  const handleStartGroup = async () => {
    let currentStoryId = storyId;
    if (!currentStoryId || currentStoryId === 'new') {
      const createdId = await createDraftStory();
      if (!createdId) return;
      currentStoryId = createdId;
    }

    try {
      await api.post(`/stories/${currentStoryId}/start-group`);
      setMessage('Group created. Opening group...');
      setTimeout(() => { setMessage(''); navigate(`/group/${currentStoryId}`); }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create group');
    }
  };

  const handleDeleteStoryDraft = async () => {
    if (!storyId || storyId === 'new' || hasActiveGroup || deletingDraft) return;

    const approved = await confirm({
      title: 'Delete Story Draft',
      message: 'Delete this draft and all of its screenplay content?',
      confirmLabel: 'Delete Story Draft',
      cancelLabel: 'Keep Draft',
      danger: true,
    });

    if (!approved) return;

    setDeletingDraft(true);
    setError('');
    try {
      await api.delete(`/stories/${storyId}`);
      setMessage('Story draft deleted');
      setTimeout(() => navigate('/home'), 700);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete story draft');
    } finally {
      setDeletingDraft(false);
    }
  };

  const handleTitleEdit = () => { setTempTitle(title); setIsEditingTitle(true); };

  const handleTitleSave = async () => {
    if (!tempTitle.trim()) { setError('Title cannot be empty'); return; }
    if (storyId && storyId !== 'new') {
      try {
        await api.put(`/stories/${storyId}/title`, { title: tempTitle });
        setTitle(tempTitle); setIsEditingTitle(false);
        setMessage('Title updated');
        setTimeout(() => setMessage(''), 2000);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to update title');
      }
    } else {
      setTitle(tempTitle); setIsEditingTitle(false);
    }
  };

  const handleTitleCancel = () => { setTempTitle(title); setIsEditingTitle(false); };

  if (loading && storyId && storyId !== 'new') {
    return (
      <div className="story-editor-container">
        <div className="loading">Loading story...</div>
      </div>
    );
  }

  return (
    <div className="story-editor-container">
      {confirmDialog}

      {/* ── Header ── */}
      <div className="story-editor-header">
        <button onClick={() => navigate('/home')} className="back-btn">
          ← {isReadOnly ? 'Group' : 'Home'}
        </button>

        <div className="title-section">
          {isEditingTitle && canEditStory ? (
            <div className="title-edit">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') handleTitleCancel();
                }}
                className="title-input"
                autoFocus
              />
              <button onClick={handleTitleSave}   className="title-save-btn">✓</button>
              <button onClick={handleTitleCancel} className="title-cancel-btn">✗</button>
            </div>
          ) : (
            <div className="title-display">
              <h1>{title}</h1>
              {canEditStory && (
                <button onClick={handleTitleEdit} className="title-edit-btn" aria-label="Edit title">✏</button>
              )}
            </div>
          )}
        </div>

        {!isOwner && (
          <span className="read-only-badge">{hasScreenplayAccess ? 'Editor' : 'View Only'}</span>
        )}

        {isOwner && hasActiveGroup && storyId && storyId !== 'new' && (
          <button
            className="se-manage-access-btn"
            onClick={() => setShowAccessModal(true)}
          >
            Manage Access
          </button>
        )}
      </div>

      {/* ── Flash messages ── */}
      {error   && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}

      {/* ── Character Panel ── */}
      {(storyId && storyId !== 'new') && (
        <div className="character-panel-wrapper">
          <CharacterPanel
            characters={characters}
            onAdd={handleAddCharacter}
            onDelete={handleDeleteCharacter}
            isReadOnly={!canEditStory}
          />
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'story'      ? 'active' : ''}`}
            onClick={() => setActiveTab('story')}
          >Story</button>
          <button
            className={`tab ${activeTab === 'screenplay' ? 'active' : ''}`}
            onClick={() => setActiveTab('screenplay')}
          >Screenplay</button>
        </div>

        <div className="tab-content">

          {/* ══ Story Tab ══ */}
          {activeTab === 'story' && (
            <div className="story-section">
              {storyType === 'written' ? (
                <>
                  <textarea
                    ref={setStoryTextareaNode}
                    className="story-textarea"
                    placeholder="Begin your story..."
                    value={storyText}
                    onChange={(e) => {
                      setStoryText(e.target.value);
                      resizeTextarea(e.currentTarget, 480);
                    }}
                    disabled={!canEditStory}
                  />
                  {storyText.trim() === '' && canEditStory && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".txt,.doc,.docx,.pdf"
                        style={{ display: 'none' }}
                      />
                      <button
                        className="upload-file-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading...' : 'Upload File'}
                      </button>
                    </>
                  )}
                  {canEditStory && (
                    <button onClick={handleSaveStory} disabled={loading} className="save-section-btn">
                      {loading ? 'Saving...' : 'Save Story'}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="uploaded-file-display">
                    <div className="file-header">
                      <div className="file-info">
                        <span className="file-icon">◻</span>
                        <span className="file-name">{uploadedFileName}</span>
                      </div>
                      {canEditStory && (
                        <button onClick={handleDeleteFile} className="delete-file-btn">Remove File</button>
                      )}
                    </div>
                    <div className="file-content">
                      {uploadedFileName.toLowerCase().endsWith('.pdf') ? (
                        <iframe src={uploadedFileUrl} className="pdf-viewer" title="PDF Viewer" />
                      ) : (
                        <div className="text-file-preview">
                          <p>File uploaded: <strong>{uploadedFileName}</strong></p>
                          <a href={uploadedFileUrl} target="_blank" rel="noopener noreferrer">
                            Open / Download File
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                  {canEditStory && (
                    <div className="file-actions">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".txt,.doc,.docx,.pdf"
                        style={{ display: 'none' }}
                      />
                      <button
                        className="upload-new-file-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? 'Uploading...' : 'Replace File'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ Screenplay Tab ══ */}
          {activeTab === 'screenplay' && (
            <div className="screenplay-section">

              {/* ── Toolbar: scene dropdown + create scene + export/group ── */}
              <div className="screenplay-header">

                {/* Left cluster: dropdown + create scene */}
                <div className="screenplay-scene-selector">
                  {scenes.length > 0 ? (
                    <select
                      className="scene-dropdown"
                      value={activeSceneId ?? ''}
                      onChange={e => setActiveSceneId(Number(e.target.value))}
                    >
                      {scenes.map(scene => (
                        <option key={scene.id} value={scene.id}>
                          Scene {scene.scene_number}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="scene-dropdown-empty">No scenes yet</span>
                  )}

                  {canEditScreenplay && (
                    <button onClick={handleAddScene} className="add-scene-btn">
                      + Create Scene
                    </button>
                  )}
                </div>

                {/* Right cluster: export + start group */}
                <div className="screenplay-toolbar-right">
                  <button onClick={handleExportScreenplay} className="export-screenplay-btn">
                    Export PDF
                  </button>
                  {isOwner && !hasActiveGroup && (
                    <button onClick={handleStartGroup} className="start-group-btn">
                      Create Group
                    </button>
                  )}
                </div>
              </div>

              {/* Access notice */}
              {!isOwner && hasScreenplayAccess && (
                <div className="se-screenplay-access-notice">
                  ✎ You have been granted story and screenplay editing access.
                </div>
              )}

              {/* ── Active scene panel ── */}
              {scenes.length === 0 ? (
                <div className="scenes-empty-state">
                  No scenes yet. Create your first scene above.
                </div>
              ) : activeScene ? (
                <div className="scene-tile">

                  {/* Scene header */}
                  <div className="scene-header">
                    <h3>Scene {activeScene.scene_number}</h3>
                    {canEditScreenplay && (
                      <button
                        onClick={() => handleDeleteScene(activeScene.id)}
                        className="delete-scene-btn"
                        aria-label="Delete scene"
                      >×</button>
                    )}
                  </div>

                  {/* Element add buttons */}
                  {canEditScreenplay && (
                    <div className="scene-actions">
                      <button onClick={() => handleAddElement(activeScene.id, 'location')}  className="add-element-btn">+ Location</button>
                      <button onClick={() => handleAddElement(activeScene.id, 'narration')} className="add-element-btn">+ Narration</button>
                      <button onClick={() => handleAddElement(activeScene.id, 'dialogue')}  className="add-element-btn">+ Dialogue</button>
                    </div>
                  )}

                  {/* Elements */}
                  <div className="elements-container">
                    {activeScene.elements.length === 0 ? (
                      <div className="elements-empty-state">
                        No elements yet. Add a location, narration, or dialogue above.
                      </div>
                    ) : (
                      activeScene.elements.map((element) => (
                        <div key={element.id} className={`element element-${element.element_type}`}>
                          <div className="element-header">
                            <span className="element-type">
                              {element.element_type === 'location'  && 'Location'}
                              {element.element_type === 'narration' && 'Narration'}
                              {element.element_type === 'dialogue'  && 'Dialogue'}
                            </span>
                            {canEditScreenplay && (
                              <button
                                onClick={() => handleDeleteElement(activeScene.id, element.id)}
                                className="delete-element-btn"
                                aria-label="Delete element"
                              >×</button>
                            )}
                          </div>

                          {element.element_type === 'dialogue' && (
                            canEditScreenplay ? (
                              characters.length === 0 ? (
                                <div className="element-no-characters">
                                  No characters yet — add characters above to assign dialogue
                                </div>
                              ) : (
                                <select
                                  className="element-input character-select"
                                  value={element.character_name || ''}
                                  onChange={e => handleElementChange(element.id, 'character_name', e.target.value)}
                                >
                                  <option value="">— Select character —</option>
                                  {characters.map(ch => (
                                    <option key={ch.id} value={ch.name}>{ch.name}</option>
                                  ))}
                                </select>
                              )
                            ) : (
                              <div className="element-character-display">
                                {element.character_name || '—'}
                              </div>
                            )
                          )}

                          <textarea
                            ref={(node) =>
                              setElementTextareaNode(
                                element.id,
                                element.element_type === 'location' ? 60 : 110,
                                node,
                              )
                            }
                            placeholder={
                              element.element_type === 'location'  ? 'INT. / EXT. — LOCATION — DAY' :
                              element.element_type === 'narration' ? 'Enter action / narration...' :
                              'Enter dialogue...'
                            }
                            value={element.content}
                            onChange={(e) => {
                              handleElementChange(element.id, 'content', e.target.value);
                              resizeTextarea(
                                e.currentTarget,
                                element.element_type === 'location' ? 60 : 110,
                              );
                            }}
                            className="element-input content-input"
                            rows={element.element_type === 'location' ? 1 : 3}
                            disabled={!canEditScreenplay}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ) : null}

            </div>
          )}

        </div>
      </div>

      {/* ── Manage Access Modal ── */}
      {showAccessModal && storyId && storyId !== 'new' && (
        <ManageAccessModal
          storyId={storyId}
          onClose={() => setShowAccessModal(false)}
        />
      )}

      {isOwner && storyId && storyId !== 'new' && !hasActiveGroup && (
        <section className="se-danger-zone">
          <div>
            <h2>Delete Story Draft</h2>
            <p>This removes the draft, uploaded file, and screenplay content.</p>
          </div>
          <button
            className="se-delete-draft-btn"
            type="button"
            onClick={handleDeleteStoryDraft}
            disabled={deletingDraft}
          >
            {deletingDraft ? 'Deleting...' : 'Delete Story Draft'}
          </button>
        </section>
      )}

    </div>
  );
};

export default StoryEditorPage;
